import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Download, RefreshCw, Folder, Trash2 } from 'lucide-react';
import TdsSidebar from '../../components/dashboard/TdsSidebar';
import { downloadGeneratedFile } from '../../utils/tdsFileHandlers';
import { useClient } from '../../contexts/ClientContext';
import { useAuth } from '../../contexts/AuthContext';
import { getStorageItem, getLegacyStorageKey } from '../../utils/storage';

const TdsGeneratedFiles = ({ isEmbedded = false }) => {
    const navigate = useNavigate();
    const { activeClient } = useClient();
    const { user } = useAuth();
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [financialYear, setFinancialYear] = useState('');
    const [quarter, setQuarter] = useState('');
    const [formType, setFormType] = useState('');
    const [tan, setTan] = useState('');
    const [files, setFiles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [activeSection, setActiveSection] = useState('generated-files');

    // Initialize from Storage
    useEffect(() => {
        const initializeData = async () => {
            const savedFinancialYear = localStorage.getItem('tdsFinancialYear')
            const savedQuarter = localStorage.getItem('tdsQuarter') || localStorage.getItem('SELECTED_QUARTER') || ''
            const savedForm = localStorage.getItem('tdsForm') || '26Q'

            if (savedFinancialYear) setFinancialYear(savedFinancialYear)
            if (savedQuarter) setQuarter(savedQuarter)
            setFormType(savedForm.toUpperCase())

            const clientId = activeClient ? activeClient._id : 'guest';
            const formPrefix = savedForm.toUpperCase();
            
            // Check new dynamic key
            const profileKey = `${formPrefix}_${clientId}_ProfileData`;
            let savedProfileData = await getStorageItem(profileKey);

            // Fallback 1: check getLegacyStorageKey which includes FY and Quarter
            if (!savedProfileData) {
                const legacyFyKey = getLegacyStorageKey(formPrefix, 'ProfileData');
                savedProfileData = localStorage.getItem(legacyFyKey);
                if (!savedProfileData) {
                    savedProfileData = await getStorageItem(legacyFyKey);
                }
            }

            // Fallback 2: very old legacy keys
            if (!savedProfileData) {
                let legacyKey = 'tdsProfileData';
                if (formPrefix === '24Q') legacyKey = 'tds24QProfileData';
                else if (formPrefix === '27Q') legacyKey = 'tds27QProfileData';
                else if (formPrefix === '27EQ') legacyKey = 'tds27EQProfileData';
                savedProfileData = await getStorageItem(legacyKey);
            }

            if (savedProfileData) {
                try {
                    const parsed = typeof savedProfileData === 'string' ? JSON.parse(savedProfileData) : savedProfileData;
                    if (parsed?.section1?.taxDeductionAccountNo) {
                        setTan(parsed.section1.taxDeductionAccountNo);
                    } else if (parsed?.section1?.tan) {
                        setTan(parsed.section1.tan);
                    } else if (parsed?.section1?.taxDeduction) {
                        setTan(parsed.section1.taxDeduction);
                    } else if (parsed?.taxDeductionAccountNo) {
                        setTan(parsed.taxDeductionAccountNo);
                    } else if (parsed?.section1?.DeductorTan) {
                        setTan(parsed.section1.DeductorTan);
                    }
                } catch (err) {
                    console.error("Error parsing profile data", err);
                }
            }
            
            // Final fallback to active client's TAN
            if (!tan && activeClient && activeClient.tan) {
                setTan(activeClient.tan);
            }
        };

        initializeData();
    }, [activeClient]);

    const handleSectionChange = (section) => {
        setActiveSection(section);

        // Check if return type is Correction or Regular
        const savedReturnType = localStorage.getItem('tdsReturnType') || localStorage.getItem('selectedReturnType') || '';
        const isCorrection = savedReturnType === 'Correction';

        // Navigate back to the Form page
        const savedForm = localStorage.getItem('tdsForm');
        let targetPath = isCorrection ? '/tds-dashboard/140-correction' : '/tds-dashboard/140'; // Default

        if (savedForm) {
            const form = savedForm.toUpperCase();
            if (form === '138' || form === '24Q') {
                targetPath = isCorrection ? (form === '138' ? '/tds-dashboard/138-correction' : '/tds-dashboard/24q-correction') : (form === '138' ? '/tds-dashboard/138' : '/tds-dashboard/24q');
            } else if (form === '144' || form === '27Q') {
                targetPath = isCorrection ? (form === '144' ? '/tds-dashboard/144-correction' : '/tds-dashboard/27q-correction') : (form === '144' ? '/tds-dashboard/144' : '/tds-dashboard/27q');
            } else if (form === '143' || form === '27EQ') {
                targetPath = isCorrection ? (form === '143' ? '/tds-dashboard/143-correction' : '/tds-dashboard/27eq-correction') : (form === '143' ? '/tds-dashboard/143' : '/tds-dashboard/27eq');
            } else if (form === '140' || form === '26Q') {
                targetPath = isCorrection ? (form === '140' ? '/tds-dashboard/140-correction' : '/tds-dashboard/26q-correction') : (form === '140' ? '/tds-dashboard/140' : '/tds-dashboard/26q');
            }
        }

        navigate(targetPath, { state: { section: section } });
    };

    const fetchFiles = async (currentTan, currentFy, currentQtr, currentForm) => {
        const t = (typeof currentTan === 'string' ? currentTan : '') || tan;
        const fy = (typeof currentFy === 'string' ? currentFy : '') || financialYear;
        const q = (typeof currentQtr === 'string' ? currentQtr : '') || quarter;
        const ft = (typeof currentForm === 'string' ? currentForm : '') || formType;

        if (!t || !fy || !q) {
            return;
        }

        setLoading(true);
        try {
            const response = await axios.post('/api/list-tds-files', {
                tan: t,
                financialYear: fy,
                quarter: q,
                formType: ft, // Optional
                userType: user ? 'Premium' : 'Guest',
                userName: user ? user.username : null
            });

            if (response.data.success) {
                setFiles(response.data.files || []);
            } else {
                toast.error("Failed to fetch files.");
            }
        } catch (error) {
            console.error("Error fetching files:", error);
            toast.error("Error fetching generated files.");
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteFile = async (filePath, fileName) => {
        if (!window.confirm(`Are you sure you want to delete ${fileName}?`)) {
            return;
        }

        try {
            const response = await axios.post('/api/delete-generated-file', { filePath });
            if (response.data.success) {
                toast.success(`Deleted file: ${fileName}`);
                fetchFiles();
            } else {
                toast.error(response.data.error || "Failed to delete file.");
            }
        } catch (error) {
            console.error("Error deleting file:", error);
            toast.error("Error deleting file.");
        }
    };

    useEffect(() => {
        const currentTan = tan || (activeClient ? activeClient.tan : '');
        if (currentTan && financialYear && quarter) {
            fetchFiles(currentTan, financialYear, quarter, formType);
        }
    }, [tan, activeClient, financialYear, quarter, formType]);

    // Generate dynamic options
    const today = new Date();
    const currMonth = today.getMonth(); // 0=Jan, 3=Apr
    const currYear = today.getFullYear();
    const currentFyStart = currMonth >= 3 ? currYear : currYear - 1;

    const financialYearOptions = [];
    for (let start = currentFyStart; start >= 2023; start--) {
        financialYearOptions.push(`${start}-${(start + 1).toString().slice(-2)}`);
    }

    let quarterOptions = ['Q1', 'Q2', 'Q3', 'Q4'];
    if (financialYear) {
        const selectedFyStart = parseInt(financialYear.split('-')[0]);
        if (selectedFyStart > currentFyStart) {
            quarterOptions = [];
        } else if (selectedFyStart === currentFyStart) {
            let maxQ = 0;
            if (currMonth >= 3 && currMonth <= 5) maxQ = 1; // Apr-Jun (Q1)
            else if (currMonth >= 6 && currMonth <= 8) maxQ = 2; // Jul-Sep (Q2)
            else if (currMonth >= 9 && currMonth <= 11) maxQ = 3; // Oct-Dec (Q3)
            else maxQ = 4; // Jan-Mar (Q4)
            quarterOptions = quarterOptions.slice(0, maxQ);
        }
    }

    const content = (
        <div className={`flex-1 w-full ${isEmbedded ? '' : 'md:ml-10 lg:ml-64'} transition-all duration-300 ${isEmbedded ? '' : 'p-8'}`}>
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-8">
                <h1 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-2">
                    <Folder className="w-6 h-6 text-blue-600" />
                    Generated Files Library
                </h1>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-6 items-end bg-gray-50 p-6 rounded-lg border border-gray-200">
                    <div className="flex flex-col">
                        <label className="text-sm font-semibold text-gray-700 mb-1">Tax Deduction Account No (TAN)</label>
                        <input
                            type="text"
                            value={tan}
                            readOnly
                            className="w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded-md text-gray-500 font-mono"
                        />
                    </div>

                    <div className="flex flex-col">
                        <label className="text-sm font-semibold text-gray-700 mb-1">Financial Year</label>
                        <select
                            value={financialYear}
                            onChange={(e) => setFinancialYear(e.target.value)}
                            className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="">Select FY</option>
                            {financialYearOptions.map(year => (
                                <option key={year} value={year}>{year}</option>
                            ))}
                        </select>
                    </div>

                    <div className="flex flex-col">
                        <label className="text-sm font-semibold text-gray-700 mb-1">Quarter</label>
                        <select
                            value={quarter}
                            onChange={(e) => setQuarter(e.target.value)}
                            className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="">Select Quarter</option>
                            {quarterOptions.map(q => (
                                <option key={q} value={q}>
                                    {q} {q === 'Q1' ? '(Apr-Jun)' : q === 'Q2' ? '(Jul-Sep)' : q === 'Q3' ? '(Oct-Dec)' : '(Jan-Mar)'}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="flex flex-col">
                        <label className="text-sm font-semibold text-gray-700 mb-1">Form Type</label>
                        <select
                            value={formType}
                            onChange={(e) => setFormType(e.target.value)}
                            className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="">Select Form</option>
                            <option value="144">144 (Payments to Non-Residents)</option>
                            <option value="27Q">27Q (Payments to Non-Residents - Legacy)</option>
                            <option value="138">138 (Salaries)</option>
                            <option value="24Q">24Q (Salaries - Legacy)</option>
                            <option value="140">140 (Other than Salaries)</option>
                            <option value="26Q">26Q (Other than Salaries - Legacy)</option>
                            <option value="143">143 (TCS)</option>
                            <option value="27EQ">27EQ (TCS - Legacy)</option>
                        </select>
                    </div>

                    <button
                        onClick={() => fetchFiles()}
                        disabled={loading || !tan}
                        className={`flex h-[42px] items-center justify-center gap-2 rounded-md px-6 font-semibold transition-all shadow-sm border
                            ${loading || !tan
                                ? 'bg-gray-50 text-gray-400 cursor-not-allowed border-gray-200'
                                : 'bg-blue-50 text-blue-700 hover:bg-blue-100 border-blue-200 active:bg-blue-200'
                            }`}
                    >
                        <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                        <span>Refresh</span>
                    </button>
                </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-left">
                        <thead className="bg-gray-50 text-gray-600 font-semibold text-sm uppercase tracking-wider border-b border-gray-200">
                            <tr>
                                <th className="px-6 py-4">File Name</th>
                                <th className="px-6 py-4">Type</th>
                                <th className="px-6 py-4">Size</th>
                                <th className="px-6 py-4">Date Modified</th>
                                <th className="px-6 py-4 text-center">Action</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {loading ? (
                                <tr>
                                    <td colSpan="5" className="px-6 py-8 text-center text-gray-500">
                                        Loading files...
                                    </td>
                                </tr>
                            ) : files.length === 0 ? (
                                <tr>
                                    <td colSpan="5" className="px-6 py-8 text-center text-gray-500 italic">
                                        No generated files found for the selected period.
                                    </td>
                                </tr>
                            ) : (
                                files.map((file) => (
                                    <tr key={file.name} className="hover:bg-blue-50/30 transition-colors">
                                        <td className="px-6 py-4 font-medium text-gray-800">{file.name}</td>
                                        <td className="px-6 py-4">
                                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium
                                        ${file.type === 'fvu' ? 'bg-purple-100 text-purple-800' :
                                                    file.type === 'csi' ? 'bg-green-100 text-green-800' :
                                                        file.type === 'zip' ? 'bg-yellow-100 text-yellow-800' :
                                                            file.type === 'pdf' ? 'bg-red-100 text-red-800' :
                                                                file.type === 'html' || file.type === 'tml' ? 'bg-blue-100 text-blue-800' :
                                                                    'bg-gray-100 text-gray-800'}`}>
                                                {file.type.toUpperCase()}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-gray-600 font-mono text-sm">{(file.size / 1024).toFixed(2)} KB</td>
                                        <td className="px-6 py-4 text-gray-600 text-sm">{new Date(file.mtime).toLocaleString()}</td>
                                            <td className="px-6 py-4 text-center whitespace-nowrap">
                                            <div className="inline-flex items-center gap-2">
                                                <button
                                                    onClick={() => {
                                                        console.log("Downloading file:", file.name, file.path);
                                                        downloadGeneratedFile(file.path);
                                                    }}
                                                    className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-50 text-blue-700 text-sm font-medium rounded-md hover:bg-blue-100 transition-colors border border-blue-200"
                                                    title="Download File"
                                                >
                                                    <Download className="w-4 h-4" />
                                                    Download
                                                </button>
                                                <button
                                                    onClick={() => handleDeleteFile(file.path, file.name)}
                                                    className="inline-flex items-center gap-1 px-3 py-1.5 bg-red-50 text-red-700 text-sm font-medium rounded-md hover:bg-red-100 transition-colors border border-red-200"
                                                    title="Delete File"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                    Delete
                                                </button>
                                            </div>
                                            </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );

    if (isEmbedded) {
        return content;
    }

    return (
        <div className="flex bg-gray-50 min-h-screen">
            <TdsSidebar
                isOpen={sidebarOpen}
                onClose={() => setSidebarOpen(false)}
                activeSection="generated-files" // Matches link match manually
                onSectionChange={handleSectionChange}
                onSidebarStateChange={() => { }}
                // passing dummy data to prevent errors if sidebar expects it
                tdsData={[]}
                challanData={[]}
            />
            {content}
        </div>
    );
};

export default TdsGeneratedFiles;
